/**
 * Botão + PeriodModal (1 mês) para filtrar OS Futura por período.
 * O modal é renderizado num portal host estável (fora do TechnicianServiceGroup)
 * para não piscar quando o App re-renderiza o board no hover.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PeriodModal } from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import './FuturaDateFilterButton.css';

function extractScheduleDate(ordem) {
  if (!ordem?.schedule) return null;
  if (typeof ordem.schedule === 'string' && /^\d{4}-\d{2}-\d{2}/.test(ordem.schedule)) {
    return ordem.schedule.split('T')[0];
  }
  try {
    const scheduleDate = new Date(ordem.schedule);
    const year = scheduleDate.getUTCFullYear();
    const month = String(scheduleDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(scheduleDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
}

function getFuturaDateRange(orders) {
  const dates = [];
  (orders || []).forEach((ordem) => {
    const dateStr = extractScheduleDate(ordem);
    if (dateStr && dateStr !== 'SEM_PREVISAO') dates.push(dateStr);
  });
  if (dates.length === 0) return { minDate: null, maxDate: null };
  dates.sort();
  return { minDate: dates[0], maxDate: dates[dates.length - 1] };
}

const portalListeners = new Set();
let portalState = { open: false };

function setPortalState(next) {
  portalState = next;
  portalListeners.forEach((listener) => listener(portalState));
}

/** Host estável — montar uma vez no App (fora do ServiceGroup). */
export function FuturaDateFilterPeriodPortal() {
  const [state, setState] = useState(portalState);

  useEffect(() => {
    portalListeners.add(setState);
    return () => {
      portalListeners.delete(setState);
    };
  }, []);

  if (!state.open) return null;

  return (
    <PeriodModal
      anchorRef={state.anchorRef}
      anchorRect={state.anchorRect}
      currentPeriod={state.currentFilter}
      minDate={state.minDate}
      maxDate={state.maxDate}
      maxRangeDays={730}
      singleMonth
      hideHeader
      hideActionsBorder
      useUserFormActions
      modalClassName="ec-module-popover futura-date-filter-period-modal"
      zIndex={10000}
      onApply={(startDate, endDate) => {
        state.onFilter?.(startDate, endDate);
      }}
      onClear={() => {
        state.onClear?.();
      }}
      onClose={() => {
        setPortalState({ open: false });
        state.onClose?.();
      }}
    />
  );
}

export function FuturaDateFilterButton({
  technician,
  orders,
  currentFilter,
  isOpen,
  onToggle,
  onFilter,
  onClear,
  onClose,
}) {
  const btnRef = useRef(null);
  const callbacksRef = useRef({ onFilter, onClear, onClose });
  callbacksRef.current = { onFilter, onClear, onClose };

  const dateRange = useMemo(() => getFuturaDateRange(orders), [orders]);
  const techKey = technician != null ? String(technician) : '';
  const filterStart = currentFilter?.startDate || null;
  const filterEnd = currentFilter?.endDate || null;

  useLayoutEffect(() => {
    if (!isOpen) {
      if (portalState.open && portalState.technicianKey === techKey) {
        setPortalState({ open: false });
      }
      return;
    }
    if (!dateRange.minDate || !dateRange.maxDate) return;

    const rect = btnRef.current?.getBoundingClientRect() || null;
    const sameOpen =
      portalState.open &&
      portalState.technicianKey === techKey &&
      portalState.minDate === dateRange.minDate &&
      portalState.maxDate === dateRange.maxDate &&
      portalState.currentFilter?.startDate === filterStart &&
      portalState.currentFilter?.endDate === filterEnd;

    // Atualiza só âncora/callbacks se já estiver aberto (evita remount do PeriodModal).
    if (sameOpen) {
      portalState.anchorRef = btnRef;
      portalState.anchorRect = rect || portalState.anchorRect;
      portalState.onFilter = (...args) => callbacksRef.current.onFilter?.(...args);
      portalState.onClear = () => callbacksRef.current.onClear?.();
      portalState.onClose = () => callbacksRef.current.onClose?.();
      return;
    }

    setPortalState({
      open: true,
      technicianKey: techKey,
      anchorRef: btnRef,
      anchorRect: rect,
      minDate: dateRange.minDate,
      maxDate: dateRange.maxDate,
      currentFilter: filterStart || filterEnd ? { startDate: filterStart, endDate: filterEnd } : null,
      onFilter: (...args) => callbacksRef.current.onFilter?.(...args),
      onClear: () => callbacksRef.current.onClear?.(),
      onClose: () => callbacksRef.current.onClose?.(),
    });
  }, [isOpen, techKey, dateRange.minDate, dateRange.maxDate, filterStart, filterEnd]);

  const handleToggle = (e) => {
    e?.stopPropagation?.();
    onToggle?.();
  };

  if (!dateRange.minDate || !dateRange.maxDate) return null;

  return (
    <i
      ref={btnRef}
      className={`bi bi-calendar3 futura-date-filter-trigger${currentFilter ? ' is-active' : ''}`}
      onClick={handleToggle}
      title="Filtrar por período"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle(e);
        }
      }}
      aria-label="Filtrar por período"
      aria-expanded={!!isOpen}
    />
  );
}

export default FuturaDateFilterButton;
